import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, User, LogOut, ChevronDown, Users } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export const Header = ({ user, transparent = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const headerClass = transparent && !scrolled
    ? "fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    : "fixed top-0 left-0 right-0 z-50 glass-header border-b border-stone-200 transition-all duration-300";

  return (
    <header className={headerClass} data-testid="header">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">H</span>
            </div>
            <span className="font-bold text-xl text-[#1A4D2E] hidden sm:block">
              Hometown Help
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/directory"
              className="text-[#4A4A4A] hover:text-[#1A4D2E] font-medium transition-colors"
              data-testid="nav-directory"
            >
              Browse Services
            </Link>
            {user && (
              <Link
                to="/my-subs"
                className="text-[#4A4A4A] hover:text-[#1A4D2E] font-medium transition-colors flex items-center gap-1"
                data-testid="nav-my-subs"
              >
                <Users className="w-4 h-4" />
                My Subs
              </Link>
            )}
            {user?.role === "provider" && (
              <Link
                to="/provider"
                className="text-[#4A4A4A] hover:text-[#1A4D2E] font-medium transition-colors"
                data-testid="nav-provider"
              >
                My Business
              </Link>
            )}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="text-[#4A4A4A] hover:text-[#1A4D2E] font-medium transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 h-10"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-[#1A4D2E] rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="hidden sm:block font-medium text-[#1A1A1A]">
                      {user.name?.split(" ")[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#71717A]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-[#1A1A1A]">{user.name}</p>
                    <p className="text-sm text-[#71717A]">{user.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs rounded-full capitalize">
                      {user.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="menu-dashboard">
                      <User className="w-4 h-4 mr-2" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-subs" className="cursor-pointer" data-testid="menu-my-subs">
                      <Users className="w-4 h-4 mr-2" />
                      My Subs
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "provider" && (
                    <DropdownMenuItem asChild>
                      <Link to="/provider" className="cursor-pointer" data-testid="menu-provider">
                        My Business
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="menu-admin">
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={handleLogin}
                  className="hidden sm:flex text-[#1A4D2E] font-medium"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={handleLogin}
                  className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full px-6 btn-hover-lift"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A1A1A]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A1A1A]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-stone-200">
            <nav className="flex flex-col gap-2">
              <Link
                to="/directory"
                className="px-4 py-3 text-[#4A4A4A] hover:bg-stone-100 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse Services
              </Link>
              {user && (
                <Link
                  to="/my-subs"
                  className="px-4 py-3 text-[#4A4A4A] hover:bg-stone-100 rounded-lg font-medium flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Users className="w-4 h-4" />
                  My Subs
                </Link>
              )}
              {user?.role === "provider" && (
                <Link
                  to="/provider"
                  className="px-4 py-3 text-[#4A4A4A] hover:bg-stone-100 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Business
                </Link>
              )}
              {user?.role === "admin" && (
                <Link
                  to="/admin"
                  className="px-4 py-3 text-[#4A4A4A] hover:bg-stone-100 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin Panel
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
